import React, { useEffect, useMemo, useState } from 'react';
import PortalShell from '@/components/portal/PortalShell';
import PageHeader from '@/components/portal/PageHeader';
import { Map, Layers, Database, Search, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { gisService, type GisLayer, type GeoDataset, type DisclosureRequestStatus } from '@/services/gisService';
import { investorService, InvestorReadError, type InvestorProfileSummary } from '@/services/investorService';
import {
  PermittedLayersPanel,
  DatasetPreviewCard,
  ClassificationBadge,
  RestrictedDataBanner,
  DisclosureRequestList,
} from '@/components/portal/access/AccessGovernance';
import { ApiConnectionStatus } from '@/components/portal/PilotDemo';
import MapViewer from '@/components/portal/gis/MapViewer';
import { PublicationStatusBadge } from '@/components/portal/gis/GisReadinessPanel';
import { PilotProductionIdentity } from '@/components/portal/investor/InvestorCentres';
import Phase2ValidationCentre from '@/components/portal/Phase2ValidationCentre';
import {
  Button,
  Card,
  CardContent,
} from '@/components/eds';





const GisDashboard: React.FC = () => {
  const { user, profile } = useAuth();
  const [layers, setLayers] = useState<GisLayer[]>([]);
  const [restrictedLayers, setRestrictedLayers] = useState<GisLayer[]>([]);
  const [datasets, setDatasets] = useState<GeoDataset[]>([]);
  const [disclosures, setDisclosures] = useState<DisclosureRequestStatus[]>([]);
  const [investor, setInvestor] = useState<InvestorProfileSummary | null>(null);
  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  const [queryResult, setQueryResult] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  // G1C1: agreement profile used for dataset access cues. 404/403 = authoritative "no investor
  // profile" (normal for staff); any other failure is UNAVAILABLE — never an uncaught rejection,
  // a fabricated profile or an inferred lock.
  const [profileState, setProfileState] = useState<'loading' | 'ok' | 'none' | 'unavailable'>('loading');
  const [profileKey, setProfileKey] = useState(0);

  useEffect(() => {
    gisService.listPermittedLayers().then((ls) => {
      setLayers(ls);
      setVisibility(Object.fromEntries(ls.map((l) => [l.id, true])));
    });
    gisService.listRestrictedLayers().then(setRestrictedLayers);
    gisService.listDatasets().then(setDatasets);
    gisService.listDisclosureRequests().then((r) => setDisclosures(r.data ?? []));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setProfileState('loading');
    investorService.getProfile().then(
      (p) => { if (!cancelled) { setInvestor(p); setProfileState('ok'); } },
      (e: unknown) => {
        if (cancelled) return;
        const st = e instanceof InvestorReadError ? e.status : 0;
        setInvestor(null);
        setProfileState(st === 404 || st === 403 ? 'none' : 'unavailable');
      },
    );
    return () => { cancelled = true; };
  }, [profileKey]);

  const toggleVisibility = (id: string) =>
    setVisibility((v) => ({ ...v, [id]: !(v[id] ?? true) }));

  const hasRestrictedDatasets = useMemo(
    () => datasets.some((d) => d.classification !== 'public'),
    [datasets],
  );

  const onQuery = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const res = await gisService.runQuery({ layerId: String(fd.get('layerId') || '') });
    setQueryResult(res.error ? `Error: ${res.error}` : `Returned ${res.data?.features?.length ?? 0} feature(s).`);
  };

  const onExport = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const res = await gisService.requestExport({
      layerId: String(fd.get('layerId') || ''),
      format: (fd.get('format') as 'geojson' | 'shp' | 'csv') || 'geojson',
    });
    setExportStatus(res.error ? `Error: ${res.error}` : `Export job queued: ${res.data?.jobId ?? '—'}`);
  };

  return (
    <PortalShell>
      <PageHeader
        title={`Welcome, ${profile?.full_name || user?.email || 'Analyst'}`}
        subtitle="GIS analyst workspace — permitted layers, classifications and disclosure workflow."
      />
      <section className="py-8">
        {/* Official environment identification — Pilot Production */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <PilotProductionIdentity />
        </div>

        {/* Pilot readiness strip */}

        {/* HF1-FIX1: the investor KYC "GIS access readiness" inference is not shown on this staff
            surface; the analyst's actual access is the server-sourced Permitted GIS Layers below. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <ApiConnectionStatus />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            { icon: Map, title: 'Permitted Layers', value: String(layers.length), color: 'text-eds-gold' },
            { icon: Layers, title: 'Datasets', value: String(datasets.length), color: 'text-eds-emerald-2' },
            { icon: Database, title: 'Disclosure Requests', value: String(disclosures.length), color: 'text-eds-muted-fg' },
          ].map((c) => (
            <Card key={c.title}><CardContent className="p-6">
              <c.icon className={`w-8 h-8 ${c.color} mb-3`} />
              <div className="text-3xl font-bold text-eds-fg font-mono">{c.value}</div>
              <div className="text-sm text-eds-muted-fg mt-1">{c.title}</div>
            </CardContent></Card>
          ))}
        </div>

        {hasRestrictedDatasets && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
            <RestrictedDataBanner />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Permitted layer catalog with visibility toggles */}
          <div className="lg:col-span-1">
            <PermittedLayersPanel layers={layers} visibility={visibility} onToggle={toggleVisibility} />
          </div>

          {/* Spatial query */}
          <Card className="lg:col-span-1"><CardContent className="p-5"><form onSubmit={onQuery} className="space-y-3">
            <h3 className="font-eds-display font-semibold text-eds-fg flex items-center gap-2">
              <Search className="w-4 h-4 text-eds-emerald-2" /> Spatial Query
            </h3>
            <select name="layerId" aria-label="Layer for spatial query" className="w-full rounded-eds-md border border-eds-border bg-eds-surface-2 px-3 py-2 text-sm text-eds-fg focus:outline-none focus:ring-2 focus:ring-eds-ring focus:border-eds-ring" required>
              <option value="">Select layer…</option>
              {layers.filter((l) => visibility[l.id] ?? true).map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
            <Button type="submit" className="w-full">
              Run query
            </Button>
            {queryResult && <p className="text-xs text-eds-muted-fg">{queryResult}</p>}
          </form></CardContent></Card>

          {/* Export request */}
          <Card className="lg:col-span-1"><CardContent className="p-5"><form onSubmit={onExport} className="space-y-3">
            <h3 className="font-eds-display font-semibold text-eds-fg flex items-center gap-2">
              <Download className="w-4 h-4 text-eds-muted-fg" /> Request Export
            </h3>
            <select name="layerId" aria-label="Layer to export" className="w-full rounded-eds-md border border-eds-border bg-eds-surface-2 px-3 py-2 text-sm text-eds-fg focus:outline-none focus:ring-2 focus:ring-eds-ring focus:border-eds-ring" required>
              <option value="">Select layer…</option>
              {layers.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <select name="format" aria-label="Export file format" className="w-full rounded-eds-md border border-eds-border bg-eds-surface-2 px-3 py-2 text-sm text-eds-fg focus:outline-none focus:ring-2 focus:ring-eds-ring focus:border-eds-ring">
              <option value="geojson">GeoJSON</option>
              <option value="shp">Shapefile</option>
              <option value="csv">CSV</option>
            </select>
            <Button type="submit" variant="secondary" className="w-full">
              Queue export
            </Button>
            {exportStatus && <p className="text-xs text-eds-muted-fg">{exportStatus}</p>}
          </form></CardContent></Card>
        </div>

        {/* Dataset preview cards with classification labels */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-eds-display font-semibold text-eds-fg flex items-center gap-2">
              <Database className="w-4 h-4 text-eds-emerald-2" /> Geological Dataset Catalog
            </h3>
            <div className="hidden md:flex items-center gap-1.5 text-xs text-eds-muted-fg">
              Classifications:
              {(['public','investor_limited','NDA_required','MOU_required'] as const).map((c) => (
                <ClassificationBadge key={c} classification={c} />
              ))}
            </div>
          </div>
          {profileState === 'unavailable' && (
            <div role="status" className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-eds-md border border-eds-gold/30 bg-eds-gold/10 px-3 py-2 text-xs text-eds-earth">
              <span>Dataset access status is temporarily unavailable. This does not mean your access has changed.</span>
              <button
                type="button"
                onClick={() => setProfileKey((k) => k + 1)}
                className="font-semibold underline underline-offset-2 rounded-eds-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
              >
                Try again
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {datasets.map((d) => (
              <div key={d.id} className="relative">
                <div className="absolute right-2 top-2 z-10">
                  <PublicationStatusBadge status={d.publicationStatus} />
                </div>
                <DatasetPreviewCard dataset={d} profile={investor} accessUnknown={profileState === 'loading' || profileState === 'unavailable'} />
              </div>
            ))}
            {datasets.length === 0 && (
              <div className="md:col-span-2 lg:col-span-3 rounded-eds-lg border border-dashed border-eds-border bg-eds-muted/30 p-5 text-sm text-eds-muted-fg" role="status">
                <p className="font-medium text-eds-fg">No GIS datasets are currently available for your access level.</p>
                <p className="mt-1">Approved datasets appear here as GIISDEC validates and releases them. Contact GIISDEC support if you believe additional access is required.</p>
              </div>
            )}

          </div>

        </div>

        {/* Controlled disclosure request status */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <DisclosureRequestList rows={disclosures} />
        </div>

        {/* Interactive GIS map viewer wired to permitted/restricted layer catalog */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <MapViewer
            layers={layers}
            restrictedLayers={restrictedLayers}
            visibility={visibility}
            onToggle={toggleVisibility}
          />
        </div>

        {/* G1C1: the PostGIS/data-integration readiness diagnostics are not shown on the normal
            analyst workspace (infrastructure detail belongs to restricted operations). */}

        {/* Phase 2 — user-facing validation support for GIS / geology users */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          <Phase2ValidationCentre audience="gis" />
        </div>


      </section>
    </PortalShell>
  );
};

export default GisDashboard;
